import { useSearch } from '../../contexts/SearchContext';
import { PropertyTypeFilterOptionLabels } from '@th/shared/src/types/SearchFilters';
import { RepliersListingTransactionType } from '@th/shared/src/types/repliers/RepliersListingTransactionType';
import { DateTime } from 'luxon';
import logEvent from '../../helpers/mutations/logEvent';
import {
  AnalyticsEventAction,
  AnalyticsEventType,
} from '@th/shared/src/types/AnalyticsEvent';
import pick from 'lodash/pick';
import isEqual from 'lodash.isequal';
import { twMerge } from 'tailwind-merge';
import SvgIcon, { IconName } from '../icons/SvgIcon';
import { featureFlags } from '../../utils/featureFlags';

type Props = {};

export default function SuggestedFiltersRow({}: Props) {
  const { searchFilters, resetAndUpdateFiltersOnTopOfExistingCoreValues } =
    useSearch();

  const isOfferDatesFiltersActive = isEqual(
    pick(searchFilters, [
      'transactionType',
      'showActive',
      'hasOfferDate',
      'minOfferDate',
    ]),
    {
      transactionType: RepliersListingTransactionType.Sale,
      showActive: true,
      hasOfferDate: true,
      minOfferDate: DateTime.now().endOf('hour').plus({ hours: 4 }).toISO(), // approx 4 hours from now
    },
  );

  const isSaleByOwnerActive = isEqual(
    pick(searchFilters, ['transactionType', 'isForSaleByOwner']),
    {
      transactionType: RepliersListingTransactionType.Sale,
      isForSaleByOwner: true,
    },
  );

  const isWaterfrontActive = isEqual(
    pick(searchFilters, ['keywords', 'propertyTypes']),
    {
      keywords: ['of waterfront'],
      propertyTypes: [PropertyTypeFilterOptionLabels.Detached],
    },
  );

  const isFixerUpperActive = isEqual(
    pick(searchFilters, ['transactionType', 'isFixerUpper', 'propertyTypes']),
    {
      transactionType: RepliersListingTransactionType.Sale,
      isFixerUpper: true,
      propertyTypes: [
        PropertyTypeFilterOptionLabels.Detached,
        PropertyTypeFilterOptionLabels.SemiDetached,
        PropertyTypeFilterOptionLabels.Multiplex,
      ],
    },
  );

  const isMansionActive = isEqual(
    pick(searchFilters, ['propertyTypes', 'minPrice']),
    {
      propertyTypes: [PropertyTypeFilterOptionLabels.Detached],
      minPrice: 5_000_000,
    },
  );

  return (
    <div className="gap-x-4 pb-2 gap-y-4 flex flex-row overflow-x-scroll flex-0 items-center justify-center">
      {featureFlags.SHOW_OFFER_DATES && (
        <FilterButton
          iconName={'PhCalendarCheck'}
          text={'Offer Dates'}
          isActive={isOfferDatesFiltersActive}
          onClick={() => {
            logEvent({
              action: AnalyticsEventAction.clicked_suggested_filter,
              type: AnalyticsEventType.suggested_filter_click,
              otherData: {
                suggestedFilterName: 'Offer Dates',
              },
            });
            resetAndUpdateFiltersOnTopOfExistingCoreValues({
              transactionType: RepliersListingTransactionType.Sale,
              showActive: true,
              showSoldOrLeased: false,
              hasOfferDate: true,
              minOfferDate: DateTime.now()
                .endOf('hour')
                .plus({ hours: 4 })
                .toISO(), // approx 4 hours from now
              minImages: 1,
            });
          }}
        />
      )}

      <FilterButton
        iconName={'PhHandshake'}
        text={'Sale By Owner'}
        isActive={isSaleByOwnerActive}
        onClick={() => {
          logEvent({
            action: AnalyticsEventAction.clicked_suggested_filter,
            type: AnalyticsEventType.suggested_filter_click,
            otherData: {
              suggestedFilterName: 'Sale By Owner',
            },
          });
          resetAndUpdateFiltersOnTopOfExistingCoreValues({
            transactionType: RepliersListingTransactionType.Sale,
            showActive: true,
            showSoldOrLeased: false,
            isForSaleByOwner: true,
            minImages: 1,
          });
        }}
      />

      <FilterButton
        iconName={'MsWater'}
        text={'Waterfront'}
        isActive={isWaterfrontActive}
        onClick={() => {
          logEvent({
            action: AnalyticsEventAction.clicked_suggested_filter,
            type: AnalyticsEventType.suggested_filter_click,
            otherData: {
              suggestedFilterName: 'Waterfront',
            },
          });
          resetAndUpdateFiltersOnTopOfExistingCoreValues({
            showActive: true,
            showSoldOrLeased: true,
            keywords: ['of waterfront'],
            propertyTypes: [PropertyTypeFilterOptionLabels.Detached],
            minImages: 1,
          });
        }}
      />
      <FilterButton
        iconName={'PhPaintbrushHousehold'}
        text={'Fixer-Uppers'}
        isActive={isFixerUpperActive}
        onClick={() => {
          logEvent({
            action: AnalyticsEventAction.clicked_suggested_filter,
            type: AnalyticsEventType.suggested_filter_click,
            otherData: {
              suggestedFilterName: 'Fixer-Uppers',
            },
          });
          resetAndUpdateFiltersOnTopOfExistingCoreValues({
            transactionType: RepliersListingTransactionType.Sale,
            showActive: true,
            showSoldOrLeased: false,
            isFixerUpper: true,
            maxPrice: 1_500_000,
            propertyTypes: [
              PropertyTypeFilterOptionLabels.Detached,
              PropertyTypeFilterOptionLabels.SemiDetached,
              PropertyTypeFilterOptionLabels.Multiplex,
            ],
            minImages: 1,
            minListDate: DateTime.now()
              .minus({ weeks: 4 })
              .toFormat('yyyy-MM-dd'), // 4 weeks ago
          });
        }}
      />

      <FilterButton
        iconName={'GiFamilyHouse'}
        text={'Mansions'}
        isActive={isMansionActive}
        onClick={() => {
          logEvent({
            action: AnalyticsEventAction.clicked_suggested_filter,
            type: AnalyticsEventType.suggested_filter_click,
            otherData: {
              suggestedFilterName: 'Mansions',
            },
          });
          resetAndUpdateFiltersOnTopOfExistingCoreValues({
            transactionType: RepliersListingTransactionType.Sale,
            showActive: true,
            showSoldOrLeased: false,
            propertyTypes: [PropertyTypeFilterOptionLabels.Detached],
            minPrice: 5_000_000,
            minImages: 1,
            minListDate: DateTime.now()
              .minus({ weeks: 4 })
              .toFormat('yyyy-MM-dd'), // 4 weeks ago
          });
        }}
      />
    </div>
  );
}

function FilterButton({
  iconName,
  text,
  onClick,
  isActive,
}: {
  iconName: IconName;
  text: string;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <button
      className="w-24 h-20 flex flex-col rounded-lg justify-center items-center flex-shrink-0 group border-0 "
      onClick={onClick}
    >
      <div className="my-2 flex flex-col justify-center items-center group">
        <SvgIcon
          name={iconName}
          className={twMerge(
            'text-gray-400 group-hover:text-text',
            isActive && 'text-text',
          )}
        />
        <p
          className={twMerge(
            'mb-0 text-center text-sm text-gray-400 group-hover:text-text',
            isActive && 'text-text',
          )}
        >
          {text}
        </p>
      </div>
      <div
        className={twMerge(
          'h-[2px] w-16 group-hover:bg-gray-400 rounded-full ',
          isActive && 'bg-gray-400',
        )}
      />
    </button>
  );
}
